
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

interface FilterOption {
  value: string;
  label: string;
}

interface AdminFiltersProps {
  filters: {
    status?: string;
    category?: string;
    difficulty?: string;
    type?: string;
  };
  onFilterChange: (key: string, value: string) => void;
  onResetFilters: () => void;
  statusOptions?: FilterOption[];
  categoryOptions?: FilterOption[];
  difficultyOptions?: FilterOption[];
  typeOptions?: FilterOption[];
}

export const AdminFilters = ({
  filters,
  onFilterChange,
  onResetFilters,
  statusOptions = [
    { value: "draft", label: "Draft" },
    { value: "published", label: "Published" },
    { value: "archived", label: "Archived" },
  ],
  categoryOptions = [],
  difficultyOptions = [
    { value: "easy", label: "Easy" },
    { value: "medium", label: "Medium" },
    { value: "hard", label: "Hard" },
  ],
  typeOptions = [],
}: AdminFiltersProps) => {
  const hasFilters = Object.values(filters).some(value => value);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <div className="min-w-[150px]">
          <Select value={filters.status || "all-status"} onValueChange={(value) => onFilterChange("status", value === "all-status" ? "" : value)}>
            <SelectTrigger>
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-status">All Status</SelectItem>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {categoryOptions.length > 0 && (
          <div className="min-w-[200px]">
            <Select value={filters.category || "all-categories"} onValueChange={(value) => onFilterChange("category", value === "all-categories" ? "" : value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-categories">All Categories</SelectItem>
                {categoryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {difficultyOptions.length > 0 && (
          <div className="min-w-[150px]">
            <Select value={filters.difficulty || "all-difficulty"} onValueChange={(value) => onFilterChange("difficulty", value === "all-difficulty" ? "" : value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-difficulty">All Difficulty</SelectItem>
                {difficultyOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {typeOptions.length > 0 && (
          <div className="min-w-[150px]">
            <Select value={filters.type || "all-types"} onValueChange={(value) => onFilterChange("type", value === "all-types" ? "" : value)}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-types">All Types</SelectItem>
                {typeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {hasFilters && (
          <Button variant="outline" onClick={onResetFilters} className="px-3">
            <X className="h-4 w-4 mr-1" />
            Reset
          </Button>
        )}
      </div>

      {hasFilters && (
        <div className="flex flex-wrap gap-2">
          {filters.status && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Status: {statusOptions.find(o => o.value === filters.status)?.label}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onFilterChange("status", "")}
              />
            </Badge>
          )}
          {filters.category && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Category: {categoryOptions.find(o => o.value === filters.category)?.label}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onFilterChange("category", "")}
              />
            </Badge>
          )}
          {filters.difficulty && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Difficulty: {filters.difficulty}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onFilterChange("difficulty", "")}
              />
            </Badge>
          )}
          {filters.type && (
            <Badge variant="secondary" className="flex items-center gap-1">
              Type: {filters.type}
              <X 
                className="h-3 w-3 cursor-pointer" 
                onClick={() => onFilterChange("type", "")}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};
